import type { AutomationProject } from '@/types/automation';
import { formatDateTime } from '@/utils/dateTime';
import { formatUserDisplayName } from '@/utils/primitives';
import { EntityOverviewHeader, type EntityOverviewHeaderProps } from './EntityOverviewHeader';

export interface ProjectOverviewHeaderProps
  extends Pick<EntityOverviewHeaderProps, 'onMoreActions' | 'onBack' | 'backLabel'> {
  /**
   * The project resource. May be undefined while the project query is still
   * resolving — fields render as "—" placeholders in that case.
   */
  project: AutomationProject | null | undefined;
  /**
   * The project name to display as the title. Accepted separately from
   * `project.name` because the URL param is reliable even before the project
   * query resolves, so the title doesn't flicker.
   */
  name: string;
  /** Number of apps in the project. */
  appCount: number;
}

/**
 * Page-level header for project-scoped pages (e.g., the apps list).
 *
 * Thin domain-aware wrapper around `EntityOverviewHeader` that knows how to
 * extract and format the standard project metadata row (region · apps count ·
 * owner · last modified). Drop into any project page; pages no longer need to
 * reach into the project shape or duplicate formatting helpers.
 */
export const ProjectOverviewHeader = ({
  project,
  name,
  appCount,
  onMoreActions,
  onBack,
  backLabel,
}: ProjectOverviewHeaderProps) => (
  <EntityOverviewHeader
    name={name}
    kindLabel="Project"
    moreActionsLabel="More project actions"
    metadataAriaLabel="Project metadata"
    {...(onMoreActions ? { onMoreActions } : {})}
    {...(onBack ? { onBack } : {})}
    {...(backLabel ? { backLabel } : {})}
    metadata={[
      <>Region: {project?.location ?? '—'}</>,
      <>Apps: {appCount}</>,
      <>Owner: {formatUserDisplayName(project?.systemData?.createdBy)}</>,
      <>
        Last modified:{' '}
        {project?.systemData?.lastModifiedAt
          ? formatDateTime(project.systemData.lastModifiedAt, 'full')
          : '—'}
      </>,
    ]}
  />
);
